import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Crown,
  Mail,
  Loader2,
  BookOpen,
  Headphones,
  Calendar,
  MessageCircleHeart,
  Download,
  ExternalLink,
  LogOut,
  Sparkles,
  FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

// Static placeholder live-session schedule for launch week. Replace with a real
// scheduler endpoint later (P2).
const THIS_WEEK_SESSIONS = [
  {
    title: 'Weekly Power Planning',
    date: 'Sun · Jun 14 · 4:00 PM MST',
    detail: 'Plan your week from your magnetic state.',
    join_url: 'https://zoom.us/j/your-meeting-id',
    icon: Calendar,
  },
  {
    title: 'Power Collective Welcome Circle',
    date: 'Thu · Jun 18 · 4:00 PM MST',
    detail: 'Meet the founding members. We open the door together.',
    join_url: 'https://zoom.us/j/your-meeting-id',
    icon: Sparkles,
  },
  {
    title: 'Weekly Power Hour Recode',
    date: 'Fri · Jun 19 · 4:00 PM MST',
    detail: 'Topic-based group Recode. Bring the pattern that\'s most alive this week.',
    join_url: 'https://zoom.us/j/your-meeting-id',
    icon: MessageCircleHeart,
  },
];

const MEDITATIONS = [
  { title: 'Morning Power Practice', duration: '7 min', detail: 'Anchor the day in magnetic state.' },
  { title: 'Evening Recode', duration: '12 min', detail: 'Release the day. Reset the field.' },
  { title: 'The Quick Catch', duration: '4 min', detail: 'Use mid-day when the old pattern fires.' },
];

export default function CirclePortalPage() {
  const [member, setMember] = useState(null);
  const [emailInput, setEmailInput] = useState(() => localStorage.getItem('circle-member-email') || '');
  const [authing, setAuthing] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchMember = useCallback(async ({ email, token } = {}) => {
    setAuthing(true);
    try {
      const body = token ? { token } : { email };
      const { data } = await axios.post(`${API}/membership/portal-access`, body);
      setMember(data);
      if (data.email) localStorage.setItem('circle-member-email', data.email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not verify membership');
      if (!token) localStorage.removeItem('circle-member-email');
    } finally {
      setAuthing(false);
    }
  }, []);

  // Auto-sign-in via magic-link token (?token=xxx) OR cached email
  useEffect(() => {
    const token = searchParams.get('token');
    const cached = localStorage.getItem('circle-member-email');
    if (!token && !cached) return undefined;
    const id = setTimeout(() => {
      if (token) {
        fetchMember({ token }).then(() => {
          // Strip the token from URL after successful auth so it doesn't get shared
          setSearchParams({}, { replace: true });
        });
      } else if (cached) {
        fetchMember({ email: cached });
      }
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (emailInput.trim()) fetchMember({ email: emailInput.trim().toLowerCase() });
  };

  const handleSignOut = () => {
    localStorage.removeItem('circle-member-email');
    setMember(null);
    setEmailInput('');
  };

  const openBillingPortal = async () => {
    if (!member?.customer_id) {
      toast.error('No billing record found. Reach out and I\'ll fix it.');
      return;
    }
    setBillingLoading(true);
    try {
      const { data } = await axios.post(`${API}/membership/billing-portal`, {
        customer_id: member.customer_id,
        return_url: `${window.location.origin}/circle`,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open billing portal');
    } finally {
      setBillingLoading(false);
    }
  };

  // ===== Gate =====
  if (!member) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 bg-background">
        <Card className="w-full max-w-md p-8 bg-card border-silver/20" data-testid="circle-gate">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-secondary/15 flex items-center justify-center">
              <Crown className="w-6 h-6 text-secondary" />
            </div>
            <h1 className="font-heading text-2xl text-foreground mb-1">The Power Collective</h1>
            <p className="text-sm text-muted-foreground">Members-only portal. Enter your email to sign in.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="circle-auth-form">
            <Input
              type="email"
              placeholder="your@email.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              autoFocus
              required
              data-testid="circle-email-input"
            />
            <Button
              type="submit"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              disabled={authing || !emailInput.trim()}
              data-testid="circle-auth-submit"
            >
              {authing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Sign In
            </Button>
          </form>
          <p className="text-xs text-muted-foreground text-center mt-5">
            Not a member yet?{' '}
            <Link to="/collective" className="text-secondary hover:underline">Join The Collective →</Link>
          </p>
        </Card>
      </div>
    );
  }

  const isFounding = member.tier === 'founding';

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="circle-portal-root">
      {/* Member header */}
      <section className="border-b border-silver/15 bg-gradient-to-br from-maroon/15 via-background to-secondary/10">
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start justify-between flex-wrap gap-4"
          >
            <div>
              {isFounding && (
                <Badge className="mb-3 bg-secondary/20 text-secondary border-secondary/40 px-3 py-1">
                  <Crown className="w-3 h-3 mr-1.5 inline" />
                  Founding Member {member.founding_spot_number ? `#${member.founding_spot_number}` : ''}
                </Badge>
              )}
              <h1 className="font-heading text-3xl md:text-4xl text-foreground leading-tight mb-1">
                Welcome back, <em className="text-secondary">{member.name}</em>.
              </h1>
              <p className="text-sm text-muted-foreground">
                Member since {fmtDate(member.member_since)} · ${member.monthly_price_usd}/mo {isFounding ? '(locked in forever)' : ''}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSignOut}
              className="text-muted-foreground"
              data-testid="circle-signout-btn"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> Sign Out
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Quick action cards */}
      <section className="border-b border-silver/15">
        <div className="container mx-auto px-4 py-10 max-w-5xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-5 bg-card border-silver/20 hover:border-secondary/40 transition-colors" data-testid="quick-ebook">
              <BookOpen className="w-6 h-6 text-secondary mb-3" />
              <h3 className="font-heading text-base text-foreground mb-1">E-Book</h3>
              <p className="text-xs text-muted-foreground mb-3">Taking Your Power Back · the full 5-Step method</p>
              <Link to="/course">
                <Button size="sm" variant="outline" className="w-full">
                  Open <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </Link>
            </Card>

            <Card className="p-5 bg-card border-silver/20 hover:border-secondary/40 transition-colors" data-testid="quick-audio">
              <Headphones className="w-6 h-6 text-secondary mb-3" />
              <h3 className="font-heading text-base text-foreground mb-1">Audio Library</h3>
              <p className="text-xs text-muted-foreground mb-3">Daily Power Practice meditations</p>
              <Button size="sm" variant="outline" className="w-full" onClick={() => document.getElementById('audio-section')?.scrollIntoView({ behavior: 'smooth' })}>
                Browse →
              </Button>
            </Card>

            <Card className="p-5 bg-card border-silver/20 hover:border-secondary/40 transition-colors" data-testid="quick-practice-app">
              <FileText className="w-6 h-6 text-secondary mb-3" />
              <h3 className="font-heading text-base text-foreground mb-1">Practice App</h3>
              <p className="text-xs text-muted-foreground mb-3">Meditations, worksheets, evening check-in</p>
              <Link to="/practice">
                <Button size="sm" variant="outline" className="w-full">
                  Open <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </Link>
            </Card>

            <Card className="p-5 bg-card border-secondary/30 hover:border-secondary transition-colors bg-gradient-to-br from-card to-secondary/5" data-testid="quick-recode">
              <MessageCircleHeart className="w-6 h-6 text-secondary mb-3" />
              <h3 className="font-heading text-base text-foreground mb-1">1-on-1 Recode</h3>
              <p className="text-xs text-muted-foreground mb-3">Book a private session with me</p>
              <a href="https://calendly.com/breahkettunen" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90">
                  Book Now <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* This week's live sessions */}
      <section className="border-b border-silver/15 bg-card/30">
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="flex items-start gap-3 mb-6">
            <Calendar className="w-5 h-5 text-secondary mt-1" />
            <div>
              <h2 className="font-heading text-2xl text-foreground">This week&apos;s live sessions</h2>
              <p className="text-sm text-muted-foreground">All times Mountain. Replays drop in the library within 24h.</p>
            </div>
          </div>
          <div className="space-y-3" data-testid="live-sessions-list">
            {THIS_WEEK_SESSIONS.map((session) => {
              const Icon = session.icon;
              return (
                <Card key={session.title} className="p-4 bg-card border-silver/20 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/15 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-foreground leading-tight">{session.title}</h3>
                    <p className="text-xs text-secondary font-semibold uppercase tracking-widest mb-1">{session.date}</p>
                    <p className="text-sm text-muted-foreground">{session.detail}</p>
                  </div>
                  <a href={session.join_url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
                      Join Link
                    </Button>
                  </a>
                </Card>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            ⚠️ Zoom links above are placeholders until I drop real ones via email on session day.
          </p>
        </div>
      </section>

      {/* Audio Library */}
      <section id="audio-section" className="border-b border-silver/15">
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="flex items-start gap-3 mb-6">
            <Headphones className="w-5 h-5 text-secondary mt-1" />
            <div>
              <h2 className="font-heading text-2xl text-foreground">Daily Power Practice — Audio Library</h2>
              <p className="text-sm text-muted-foreground">Guided meditations recorded for you. New tracks added monthly.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-3" data-testid="audio-library">
            {MEDITATIONS.map((m) => (
              <Card key={m.title} className="p-5 bg-card border-silver/20">
                <Badge variant="outline" className="mb-3 text-[10px] border-secondary/30 text-secondary">{m.duration}</Badge>
                <h3 className="font-heading text-lg text-foreground mb-1">{m.title}</h3>
                <p className="text-xs text-muted-foreground mb-4">{m.detail}</p>
                <Button size="sm" variant="outline" className="w-full" disabled>
                  ▶ Uploading this week
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Membership / billing */}
      <section>
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <Card className="p-6 bg-card border-silver/20" data-testid="membership-card">
            <h2 className="font-heading text-xl text-foreground mb-4">Your Membership</h2>
            <div className="grid sm:grid-cols-3 gap-4 mb-5">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Tier</p>
                <p className="text-base text-foreground font-medium capitalize">{member.tier} Member</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Monthly</p>
                <p className="text-base text-foreground font-medium">${member.monthly_price_usd}/mo</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Status</p>
                <p className="text-base text-foreground font-medium capitalize">{member.status}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={openBillingPortal}
              disabled={billingLoading || !member.customer_id}
              data-testid="billing-portal-btn"
            >
              {billingLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5 mr-1.5" />}
              Manage Subscription (Stripe)
            </Button>
            <p className="text-xs text-muted-foreground mt-3">
              Need help? Email <a href="mailto:takingyourpowerback@breahkettunen.com" className="text-secondary hover:underline">takingyourpowerback@breahkettunen.com</a> — replies come straight to me.
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
}
